import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  ListChecks,
  Plus,
  Search,
  Edit,
  Eye,
  Power,
  TrendingUp,
  Clock,
  Target,
  MoreHorizontal,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { generateMockStrategies } from '@/lib/mock-data/strategies-mock-data';
import type { CollectionStrategyItem } from '@/types/collection-strategies';

const StrategiesPage: React.FC = () => {
  const navigate = useNavigate();
  const [strategies] = useState<CollectionStrategyItem[]>(generateMockStrategies());
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // Filter strategies
  const filteredStrategies = useMemo(() => {
    let filtered = strategies;

    if (filterType !== 'all') {
      filtered = filtered.filter(s => s.type === filterType);
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(s => s.status === filterStatus);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s =>
        s.name.toLowerCase().includes(query) ||
        s.description.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [strategies, filterType, filterStatus, searchQuery]);

  const handleCreateStrategy = () => {
    navigate('/settings/strategies/create', { state: { mode: 'create' } });
  };

  const handleEditStrategy = (strategy: CollectionStrategyItem) => {
    navigate('/settings/strategies/edit', { state: { strategy, mode: 'edit' } });
  };

  const handleToggleStatus = (strategy: CollectionStrategyItem) => {
    const newStatus = strategy.status === 'Active' ? 'Inactive' : 'Active';
    toast.success(`Strategy ${newStatus === 'Active' ? 'activated' : 'deactivated'} successfully`);
  };

  const getTypeBadge = (type: CollectionStrategyItem['type']) => {
    const colors: Record<CollectionStrategyItem['type'], string> = {
      Standard: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      Intensive: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      VIP: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      'High Risk': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    };
    return <Badge className={colors[type]}>{type}</Badge>;
  };

  const getPerformanceBadge = (successRate: number) => {
    if (successRate >= 80) return <Badge className="bg-green-100 text-green-800">Excellent</Badge>;
    if (successRate >= 60) return <Badge className="bg-blue-100 text-blue-800">Good</Badge>;
    if (successRate >= 40) return <Badge className="bg-amber-100 text-amber-800">Average</Badge>;
    return <Badge className="bg-red-100 text-red-800">Poor</Badge>;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <ListChecks className="h-8 w-8" />
            Collection Strategies
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure automated collection workflows and assignment rules
          </p>
        </div>
        <Button onClick={handleCreateStrategy}>
          <Plus className="h-4 w-4 mr-2" />
          Create Strategy
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search strategies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="Standard">Standard</SelectItem>
            <SelectItem value="Intensive">Intensive</SelectItem>
            <SelectItem value="VIP">VIP</SelectItem>
            <SelectItem value="High Risk">High Risk</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Strategies Table */}
      <Card>
        <CardHeader>
          <CardTitle>Strategies ({filteredStrategies.length})</CardTitle>
          <CardDescription>
            Manage your collection strategies and monitor their performance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name & Description</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Active Cases</TableHead>
                <TableHead>Success Rate</TableHead>
                <TableHead>Avg Days</TableHead>
                <TableHead>Performance</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStrategies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    No strategies found matching your criteria
                  </TableCell>
                </TableRow>
              ) : (
                filteredStrategies.map((strategy) => (
                  <TableRow key={strategy.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{strategy.name}</div>
                        <div className="text-sm text-muted-foreground max-w-md truncate">
                          {strategy.description}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getTypeBadge(strategy.type)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={strategy.status === 'Active'}
                          onCheckedChange={() => handleToggleStatus(strategy)}
                        />
                        <span className="text-sm">
                          {strategy.status === 'Active' ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-muted-foreground" />
                        {strategy.activeCasesCount}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-green-600" />
                        {strategy.successRate}%
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        {strategy.avgDaysToPayment} days
                      </div>
                    </TableCell>
                    <TableCell>{getPerformanceBadge(strategy.successRate)}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditStrategy(strategy)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit Strategy
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Eye className="h-4 w-4 mr-2" />
                            View Performance
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleStatus(strategy)}>
                            <Power className="h-4 w-4 mr-2" />
                            {strategy.status === 'Active' ? 'Deactivate' : 'Activate'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default StrategiesPage;
